import _extends from '@babel/runtime/helpers/extends';
import React__default from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { getIntentClass } from '../../commonClasses.js';

const SpinnerSize = {
  SMALL: 20,
  STANDARD: 50,
  LARGE: 100
};

// see http://stackoverflow.com/a/18473154/3124288 for calculating arc path
const R = 45;
const SPINNER_TRACK = `M 50,50 m 0,-${R} a ${R},${R} 0 1 1 0,${R * 2} a ${R},${R} 0 1 1 0,-${R * 2}`;

// unitless total length of SVG path, to which stroke-dash* properties are relative.
// https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/pathLength
// this value is the result of `<path d={SPINNER_TRACK} />.getTotalLength()` and works in all browsers:
const PATH_LENGTH = 280;
const MIN_SIZE = 10;
const STROKE_WIDTH = 4;
const MIN_STROKE_WIDTH = 12;
const BASE_CLASS = 'nui-spinner';
const Classes = {
  BASE: BASE_CLASS,
  NO_SPIN: `${BASE_CLASS}--no-spin`,
  ANIMATION: `${BASE_CLASS}-animation`,
  TRACK: `${BASE_CLASS}-track`,
  HEAD: `${BASE_CLASS}-head`
};

/**
 * Clamps the given number between min and max values. Returns value if within
 * range, or closest bound.
 */
function clamp(val, min, max) {
  if (max < min) {
    throw new Error('max cannot be less than min');
  }
  return Math.min(Math.max(val, min), max);
}
const spinnerPropTypes = {
  /** Width and height of the spinner in pixels */
  size: PropTypes.number,
  /** Defines small size of the spinner */
  small: PropTypes.bool,
  /** Defines large size of the spinner */
  large: PropTypes.bool,
  className: PropTypes.string,
  intent: PropTypes.oneOf(['primary', 'danger', 'success', 'warning']),
  /** A value between 0 and 1 (inclusive) representing how far along the operation is */
  value: PropTypes.number
};
/**
 * Spinner component.
 */
function Spinner({
  className = '',
  small,
  large,
  intent = 'primary',
  value,
  size,
  ...htmlProps
}) {
  /**
   * Resolve size to a pixel value.
   * Size can be set by small, large, props, default, or minimum constant.
   */
  const getSize = () => {
    if (size == null) {
      if (small) {
        return SpinnerSize.SMALL;
      }
      if (large) {
        return SpinnerSize.LARGE;
      }
      return SpinnerSize.STANDARD;
    }
    return Math.max(MIN_SIZE, size);
  };
  const spinnerSize = getSize();

  // keep spinner track width consistent at all sizes (down to about 10px).
  const strokeWidth = Math.min(MIN_STROKE_WIDTH, STROKE_WIDTH * SpinnerSize.LARGE / spinnerSize);
  const strokeOffset = PATH_LENGTH - PATH_LENGTH * (value == null ? 0.25 : clamp(value, 0, 1));

  /** Compute viewbox such that stroked track sits exactly at edge of image frame. */
  const getViewBox = () => {
    const radius = R + strokeWidth / 2;
    const viewBoxX = (50 - radius).toFixed(2);
    const viewBoxWidth = (radius * 2).toFixed(2);
    return `${viewBoxX} ${viewBoxX} ${viewBoxWidth} ${viewBoxWidth}`;
  };

  // multiple DOM elements around SVG are necessary to properly isolate animation:
  // - SVG elements in IE do not support anim/trans so they must be set on a parent HTML element.
  // - SPINNER_ANIMATION isolates svg from parent display and is always centered inside root element.
  return /*#__PURE__*/React__default.createElement("div", _extends({}, htmlProps, {
    "aria-valuemax": 100,
    "aria-valuemin": 0,
    "aria-valuenow": typeof value === 'number' ? value * 100 : undefined,
    "aria-label": "Spinner",
    className: cx(Classes.BASE, getIntentClass(intent), {
      [Classes.NO_SPIN]: value != null
    }, className),
    role: "progressbar"
  }), /*#__PURE__*/React__default.createElement("div", {
    className: Classes.ANIMATION
  }, /*#__PURE__*/React__default.createElement("svg", {
    width: spinnerSize,
    height: spinnerSize,
    strokeWidth: strokeWidth.toFixed(2),
    viewBox: getViewBox()
  }, /*#__PURE__*/React__default.createElement("path", {
    className: Classes.TRACK,
    d: SPINNER_TRACK
  }), /*#__PURE__*/React__default.createElement("path", {
    className: Classes.HEAD,
    d: SPINNER_TRACK,
    pathLength: PATH_LENGTH,
    strokeDasharray: `${PATH_LENGTH} ${PATH_LENGTH}`,
    strokeDashoffset: strokeOffset
  }))));
}
Spinner.propTypes = spinnerPropTypes;
Spinner.displayName = 'NuiSpinner';

export { Classes, SpinnerSize, clamp, Spinner as default };
